import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalProps,
  Platform,
  ScrollView,
  useWindowDimensions,
  View
} from 'react-native';
import styled from 'styled-components/native';
import * as Yup from 'yup';

import { useNavigation } from '@react-navigation/core';
import ContentView from '../../../components/ContentView';
import Form from '../../../components/form/Form';
import FormButton from '../../../components/form/FormButton';
import FormCheckbox from '../../../components/form/FormCheckbox';
import FormInput from '../../../components/form/FormInput';
import Link from '../../../components/Link';
import Spacer from '../../../components/Spacer';
import { Caption, H1, P, Subtitle } from '../../../components/Typography';
import Colors from '../../../constants/Colors';
import { Feather } from '@expo/vector-icons';
import IconButton from '../../../components/IconButton';
import useLogin from '../../../hooks/user/useLogin';
import useSignUp from '../../../hooks/user/useSignUp';
import { StackNavigationProp } from '@react-navigation/stack';
import * as LocalAuthentication from 'expo-local-authentication';
import EnableBiometricsModal from './EnableBiometricsModal';
import FormMaskedInput from '../../../components/form/FormMaskedInput';
import { internationalPhoneMask } from '../../../phone-number/InternationalPhoneNumberMask';

const passwordMinLength = 8;
const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/;
const passwordRegexErrorMessage =
  'Need at least an uppercase and a lowercase letter, and a number.';

const StyledDimContainer = styled.View`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
`;

const StyledDialog = styled.KeyboardAvoidingView<{ isMobile: boolean }>`
  width: ${p => (p.isMobile ? 100 : 90)}%;
  height: ${p => (p.isMobile ? 100 : 90)}%;
  border-radius: 10px;
  max-width: 1080px;
  max-height: ${p => (p.isMobile ? '100%' : '627px')};
  overflow: hidden;
  background-color: ${Colors.primaryPurple};
`;

const StyledImageBackground = styled.ImageBackground<{ isDesktop: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${p => (p.isDesktop ? '100%' : '130%')};
  height: 100%;
  max-height: 100%;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'ios' ? 48 : 20}px;
  right: 20px;
  z-index: 1;
`;

const StyledContainerView = styled.View<{ isDesktop: boolean }>`
  flex-direction: ${p => (p.isDesktop ? 'row' : 'column')};
`;

const StyledContainerColumn = styled.View<{ isDesktop: boolean }>`
  flex: ${p => (p.isDesktop ? 1 : '0 1 auto')};
  align-items: center;
`;

const StyledHeaderView = styled.View<{ isDesktop: boolean }>`
  padding: ${p =>
    p.isDesktop
      ? '124px 0 0 0'
      : `${Platform.OS === 'ios' ? 64 : 37}px 24px 0 24px`};
  max-width: ${p => (p.isDesktop ? 320 : 430)}px;
  width: 100%;
`;

const StyledH1 = styled(H1)`
  max-width: 320px;
`;

const StyledFormView = styled.View<{ isDesktop: boolean }>`
  padding-top: ${p => (p.isDesktop ? 48 : 0)}px;
  max-width: 430px;
  width: 100%;
`;

const StyledRowView = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledCaption = styled(Caption)`
  margin-right: 8px;
`;

export type SignUpModalProps = ModalProps & {
  onRequestLogin?: () => void;
};

const SignUpModal: React.FC<SignUpModalProps> = ({
  onRequestLogin,
  ...props
}) => {
  const { onRequestClose } = props;
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const isMobile = window.width < 468;
  const formikRef = useRef<FormikProps<any> | null>();
  // const navigation =
  //   useNavigation<StackNavigationProp<{ DrawerNavigator: {} }>>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { createdUser, creatingUser, createError, createUser } = useSignUp();
  const { jwtToken, login } = useLogin();
  const [showBiometric, setShowBiometric] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const verifyEnrolledBiometric = async () => {
    const savedBiometrics = await LocalAuthentication.isEnrolledAsync();
    setHasBiometric(savedBiometrics);
  };

  useEffect(() => {
    if (Platform.OS !== 'web') verifyEnrolledBiometric();
  }, []);

  // enable/disable form button loading spinner
  useEffect(() => {
    // continue to keep the form disabled after creating the user so that
    // user can't change email field and create another account (to prevent
    // spamming the server)
    formikRef.current?.setSubmitting(Boolean(createdUser) || creatingUser);
  }, [createdUser, creatingUser]);

  // handle create error
  useEffect(() => {
    if (createError) {
      formikRef.current?.setFieldError('email', createError.message);
    }
  }, [createError]);

  // after creating the user login
  useEffect(() => {
    if (createdUser?.email) {
      login({
        email: createdUser.email,
        password: formikRef.current?.values.password
      });
    }
  }, [createdUser, login]);

  // after logging in redirect to Welcome Screen
  useEffect(() => {
    if (jwtToken) {
      if (!hasBiometric) {
        navigateToWelcomeScreen();
      } else {
        setShowBiometric(true);
      }
    }
  }, [createdUser, jwtToken, navigation]);

  function navigateToWelcomeScreen() {
    navigation.replace('WelcomeNavigator', {
      screen: 'Welcome'
    });
  }

  return (
    <Modal animationType='fade' transparent {...props}>
      {showBiometric && (
        <EnableBiometricsModal
          onRequestClose={() => navigateToWelcomeScreen()}
        />
      )}
      {!showBiometric && (
        <StyledDimContainer>
          <StyledDialog
            isMobile={isMobile}
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          >
            <ScrollView scrollIndicatorInsets={{ right: 1 }}>
              <StyledImageBackground
                isDesktop={isDesktop}
                resizeMode='stretch'
                source={require('../../../assets/sign-up/abstract-background.png')}
              />
              <StyledIconButton onPress={event => onRequestClose?.(event)}>
                <Feather name='x' size={28} color='white' />
              </StyledIconButton>

              <StyledContainerView isDesktop={isDesktop}>
                <StyledContainerColumn isDesktop={isDesktop}>
                  <StyledHeaderView isDesktop={isDesktop}>
                    <StyledH1 color='light' padding={2}>
                      Chloe Platform Sign-Up
                    </StyledH1>
                    <Subtitle color='light'>
                      Sign up to experiment on yourself or to join other studies
                      on the Chloe study platform.
                    </Subtitle>
                  </StyledHeaderView>
                </StyledContainerColumn>
                <StyledContainerColumn isDesktop={isDesktop}>
                  <StyledFormView isDesktop={isDesktop}>
                    <Formik
                      innerRef={formik => (formikRef.current = formik)}
                      initialValues={{
                        firstName: '',
                        lastName: '',
                        email: '',
                        password: '',
                        confirmPassword: '',
                        legal: false,
                        phoneNumber: '',
                        notifyForMessages: true,
                        notifyForStudy: true
                      }}
                      validationSchema={Yup.object().shape({
                        firstName: Yup.string().required(
                          'First name is required.'
                        ),
                        lastName: Yup.string().required(
                          'Last name is required.'
                        ),
                        email: Yup.string()
                          .email()
                          .required('Email is required.'),
                        password: Yup.string()
                          .matches(passwordRegex, passwordRegexErrorMessage)
                          .min(
                            passwordMinLength,
                            `Minimum ${passwordMinLength} characters.`
                          ),
                        confirmPassword: Yup.string()
                          .required('Password confirmation is required.')
                          .oneOf(
                            [Yup.ref('password'), null],
                            'Passwords must match.'
                          ),
                        legal: Yup.bool().oneOf(
                          [true],
                          'Accept Terms of Service and Privacy Policy is required'
                        ),
                        phoneNumber: Yup.string().test(
                          'exactLength',
                          'Must be a valid phone number',
                          text => {
                            if (text) {
                              let matched = false;
                              let lengthMatched = 0;
                              internationalPhoneMask.forEach(data => {
                                if (
                                  text &&
                                  text
                                    .trim()
                                    .replace(/[^0-9+]/g, '')
                                    .includes(data.dial_code)
                                ) {
                                  if (
                                    data.mask &&
                                    data.dial_code.length >= lengthMatched
                                  ) {
                                    lengthMatched = data.dial_code.length;
                                    matched = true;

                                    if (
                                      !data.min &&
                                      text.trim().replace(/[^0-9+]/g, '')
                                        .length ===
                                        data.mask.trim().replace(/[^0-9+]/g, '')
                                          .length
                                    ) {
                                      return matched;
                                    } else if (
                                      data.min &&
                                      text.trim().replace(/[^0-9+]/g, '')
                                        .length >=
                                        data.min + 1
                                    ) {
                                      return matched;
                                    } else {
                                      matched = false;
                                      return matched;
                                    }
                                  }
                                } else {
                                  if (!matched) {
                                    matched = false;
                                    return matched;
                                  }
                                }
                              });

                              return matched;
                            } else {
                              return true;
                            }
                          }
                        ),
                        notifyForMessages: Yup.bool(),
                        notifyForStudy: Yup.bool()
                      })}
                      onSubmit={formValues => {
                        if (!isPhoneNumberValid) {
                          formValues.notifyForMessages = false;
                          formValues.notifyForStudy = false;
                        }
                        createUser(formValues);
                      }}
                    >
                      <Form>
                        <ContentView>
                          <FormInput
                            name='firstName'
                            // label='First Name'
                            placeholder='First name'
                          />
                          <FormInput
                            name='lastName'
                            // label='Last Name'
                            placeholder='Last name'
                          />
                          <FormInput
                            autoCapitalize='none'
                            autoCorrect={false}
                            keyboardType='email-address'
                            name='email'
                            // label='Email'
                            placeholder='Email address'
                          />
                          <FormMaskedInput
                            name='phoneNumber'
                            inputType='phone'
                            placeholder='Phone Number (optional)'
                            isValid={isValid => {
                              if (isValid) {
                                setIsPhoneNumberValid(true);
                              } else {
                                setIsPhoneNumberValid(false);
                              }
                            }}
                          />
                          <StyledRowView>
                            <View>
                              <FormCheckbox
                                disabled={!isPhoneNumberValid}
                                name='notifyForMessages'
                                label={
                                  <StyledCaption color='light'>
                                    Text for messages
                                  </StyledCaption>
                                }
                              />
                              <Spacer size='small' />
                            </View>
                            <View>
                              <FormCheckbox
                                disabled={!isPhoneNumberValid}
                                name='notifyForStudy'
                                label={
                                  <Caption color='light'>
                                    Text for study notifications
                                  </Caption>
                                }
                              />
                            </View>
                          </StyledRowView>
                          <Spacer size='medium' />
                          <FormInput
                            secureTextEntry
                            name='password'
                            // label='Password'
                            placeholder='Password'
                            textContentType='oneTimeCode'
                          />
                          <FormInput
                            secureTextEntry
                            name='confirmPassword'
                            returnKeyType='go'
                            submitOnReturn={true}
                            // label='Confirm Password'
                            placeholder='Confirm password'
                            textContentType='oneTimeCode'
                          />
                          <Spacer size='small' />
                          <FormCheckbox
                            name='legal'
                            label={
                              <Caption color='light'>
                                I have read and agree to the{' '}
                                <Link
                                  onPress={() => navigation.navigate('Terms')}
                                >
                                  Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link
                                  onPress={() =>
                                    navigation.navigate('PrivacyPolicy')
                                  }
                                >
                                  Privacy Policy
                                </Link>
                              </Caption>
                            }
                          />
                          <Spacer size='large' />
                          <FormButton inverse outlined>
                            Sign Up
                          </FormButton>
                          <Spacer />
                          <P center color='light'>
                            Already have an account?{' '}
                            <Link onPress={onRequestLogin}>Log In</Link>
                          </P>
                          <Spacer size={4} />
                        </ContentView>
                      </Form>
                    </Formik>
                  </StyledFormView>
                </StyledContainerColumn>
              </StyledContainerView>
            </ScrollView>
          </StyledDialog>
        </StyledDimContainer>
      )}
    </Modal>
  );
};

export default SignUpModal;
